import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "@/components/Motion";
import { useSeo } from "@/hooks/useSeo";
import {
  KITCHEN_LAYOUTS,
  KITCHEN_ANATOMY,
  WARDROBES,
  WARDROBE_INTERNALS,
  PROCESS,
  STUDIO_BRANDS,
} from "@/data/kitchens";

const ENQUIRE_HREF = "/contact?stone=Modular%20Kitchen%20%2F%20Wardrobe%20Studio";

export default function KitchensWardrobes() {
  useSeo({
    title: "Modular Kitchens & Wardrobes | Varun Marbles",
    description:
      "Design-and-build modular kitchens and wardrobes by Varun Marbles — Häfele & Hettich hardware, factory-finished shutters, and countertops cut from our own KalingaStone and Paradigm quartz slabs.",
    path: "/kitchens-wardrobes",
  });

  return (
    <>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-16 md:pb-24">
        <FadeIn delay={0.1}>
          <p data-testid="kw-overline" className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-8">
            Kitchens & Wardrobes
          </p>
        </FadeIn>
        <h1 data-testid="kw-headline" className="font-serif font-light tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskedLines
            delay={0.2}
            lines={[<>Fitted rooms,</>, <>built around <span className="italic">stone</span>.</>]}
          />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-10 max-w-2xl text-lg md:text-xl font-light leading-relaxed text-foreground/85" data-testid="kw-lead">
            A Varun Marbles kitchen begins where most end — with the stone. We design, manufacture
            and install modular kitchens and wardrobes end to end, and cut every countertop from
            our own slabs.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link
              to={ENQUIRE_HREF}
              data-testid="kw-consult-btn"
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground/80"
            >
              Book a design consultation
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/collections/quartz-slabs"
              data-testid="kw-quartz-link"
              className="group inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              See the quartz we build on
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Hero image */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Reveal delay={0.15}>
          <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden">
            <img
              src="/assets/kitchens/hero-kitchen.jpg"
              alt="Modular kitchen with bookmatched stone island by Varun Marbles"
              data-testid="kw-hero-image"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]"
            />
          </div>
          <p className="mt-4 text-xs tracking-[0.25em] uppercase text-secondary-foreground">
            Bookmatched stone island, designed and fitted by us
          </p>
        </Reveal>
      </section>

      {/* Layouts */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32" data-testid="kw-layouts">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">Modular kitchens</p>
        </FadeIn>
        <h2 className="font-serif font-light tracking-tighter leading-tight text-3xl md:text-5xl max-w-2xl">
          Six ways to plan a kitchen
        </h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {KITCHEN_LAYOUTS.map((l, i) => (
            <Reveal key={l.name} delay={0.06 * (i % 3)}>
              <figure data-testid={`kw-layout-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={l.image}
                    alt={`${l.name} modular kitchen`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="mt-5">
                  <h3 className="font-serif text-2xl font-light tracking-tight">{l.name}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-muted-foreground">{l.note}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Anatomy */}
      <section className="bg-secondary/40" data-testid="kw-anatomy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">No compromise inside</p>
          </FadeIn>
          <h2 className="font-serif font-light tracking-tighter leading-tight text-3xl md:text-5xl max-w-2xl">
            Anatomy of a Varun kitchen
          </h2>
          <div className="mt-14">
            {KITCHEN_ANATOMY.map((a, i) => (
              <Reveal key={a.title} delay={0.05 * i}>
                <div
                  className="grid md:grid-cols-12 gap-4 md:gap-10 py-8 border-t border-border last:border-b"
                  data-testid={`kw-anatomy-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <p className="md:col-span-2 font-serif text-xl font-light text-secondary-foreground">
                    0{i + 1}
                  </p>
                  <h3 className="md:col-span-3 font-serif text-2xl font-light tracking-tight">{a.title}</h3>
                  <p className="md:col-span-7 text-sm md:text-base font-light leading-relaxed text-muted-foreground">
                    {a.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Wardrobes */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="kw-wardrobes">
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">Wardrobes</p>
        </FadeIn>
        <h2 className="font-serif font-light tracking-tighter leading-tight text-3xl md:text-5xl max-w-2xl">
          Wardrobes, fitted to the millimetre
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-x-10 gap-y-16">
          {WARDROBES.map((w, i) => (
            <Reveal key={w.name} delay={0.06 * i}>
              <figure data-testid={`kw-wardrobe-${w.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={w.image}
                    alt={`${w.name} wardrobe`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="mt-5">
                  <h3 className="font-serif text-2xl font-light tracking-tight">{w.name}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-muted-foreground">{w.note}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-14 max-w-3xl text-base font-light leading-relaxed text-foreground/80 border-l-2 border-primary pl-6" data-testid="kw-wardrobe-internals">
            {WARDROBE_INTERNALS}
          </p>
        </Reveal>
      </section>

      {/* Process */}
      <section className="bg-secondary/40" data-testid="kw-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">How it works</p>
          </FadeIn>
          <h2 className="font-serif font-light tracking-tighter leading-tight text-3xl md:text-5xl max-w-2xl">
            From first visit to final shelf
          </h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={0.06 * i}>
                <div data-testid={`kw-process-${i + 1}`} className="border-t border-border pt-6">
                  <p className="font-serif text-4xl font-light text-secondary-foreground/70">0{i + 1}</p>
                  <h3 className="mt-4 font-serif text-xl font-light tracking-tight">{p.step}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-muted-foreground">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 md:mt-24 border-y border-border py-10 md:py-14" data-testid="kw-brands">
              <p className="text-center text-[10px] tracking-[0.3em] uppercase text-secondary-foreground mb-8 md:mb-12">
                Built with
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-10 md:gap-x-6">
                {STUDIO_BRANDS.map((b) => (
                  <img
                    key={b.name}
                    src={b.img}
                    alt={b.name}
                    data-testid={`kw-brand-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`${b.h} w-auto object-contain opacity-85 transition-opacity duration-500 hover:opacity-100`}
                  />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-serif font-light tracking-tighter leading-tight text-3xl md:text-5xl">
              <MaskedLines lines={[<>Tell us about your kitchen —</>, <>we'll bring the <span className="italic">stone</span>.</>]} />
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.15}>
              <Link
                to={ENQUIRE_HREF}
                data-testid="kw-cta-btn"
                className="group inline-flex items-center gap-2 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-foreground/80"
              >
                Start your enquiry
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <p className="mt-4 text-xs tracking-[0.15em] uppercase text-muted-foreground">
                or call +91 97735 58193
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
